<template>
  <BittsModal
    :model-value="visible"
    aria-label="Billing Details"
    class="view-billing-modal"
    :primary-button-text="isOnBillingSummary ? 'Done' : 'Save'"
    :width="700"
    @action="onModalAction"
    @closed="emit('closed')"
  >
    <template #header>
      <h1>Billing Details</h1>
    </template>
    <BittsTabs
      :current-value="currentTab"
      :tabs="BILLING_TABS"
      @change:tab="onChangeTab"
    />
    <div v-if="loading" class="flex justify-center items-center h-[240px]">
      <img class="w-64" src="/images/gifs/loading.gif" alt="Loading" />
    </div>
    <div
      v-show="!loading"
      role="tabpanel"
      :aria-label="
        isOnBillingSummary ? 'Billing Summary' : 'Edit Payment Method'
      "
    >
      <section v-if="isOnBillingSummary" class="view-billing-modal__tab">
        <div class="view-billing-modal__summary-section">
          <h3>Plan Status</h3>
          <div class="flex items-center gap-8">
            <p
              >{{ isSubscriptionCancelled ? 'Ends' : 'Renews' }} on
              {{ subscriptionRenewalDate }}</p
            >
            <AccountStatusTag :subscription-status="subscriptionStatus || ''" />
          </div>
        </div>
        <div
          v-if="!isSubscriptionCancelled"
          class="view-billing-modal__summary-section"
          ><h3>Next Invoice</h3
          ><p
            >{{ subscriptionNextBillingDate }} •
            <span class="font-bold text-success-text">{{ cost }}</span></p
          ></div
        >
        <div v-else class="view-billing-modal__summary-section"
          ><h3>Last Invoice Paid</h3
          ><p
            >{{ lastPaidDate }} •
            <span class="font-bold text-success-text">{{
              lastPaidAmountReadable
            }}</span></p
          ></div
        >
        <div class="view-billing-modal__summary-section"
          ><h3>Payment Method</h3>
          <p class="flex items-center gap-8">
            <FontAwesomeIcon
              :icon="['fak', 'credit-card']"
              :style="{ height: '18px', width: '18px' }"
              class="text-neutral-accent"
            />
            {{ creditCardText }}
            <BittsButton
              variant="ghost"
              type="neutral"
              size="x-small"
              :center-icon="['fak', 'edit']"
              aria-label="Edit payment method"
              class="h-18 w-18"
              @click="currentTab = EDIT_PAYMENT_METHOD"
            />
          </p>
        </div>
        <BittsButton
          text="View invoices"
          type="neutral"
          variant="outline"
          data-testid="edit-payment"
          class="self-end mt-20"
          size="x-small"
          @click="handleViewInvoices"
        />
        <SeatBreakdown
          v-if="onLegacySubscription"
          :billing-period
          class="mt-20"
        />
      </section>
      <section v-else
        ><p class="pt-40">
          {{
            `This new card will be used for your next invoice and your current card ${creditCardText} will be deleted`
          }}
        </p>
        <ChargifyIframes
          :chargify-client
          :editing="true"
          @loaded="onLoaded"
          @form-ref-saved="(form) => (chargifyForm = form)"
      /></section>
    </div>
  </BittsModal>
</template>

<script setup lang="ts">
import { BittsButton, BittsModal, BittsTabs } from '@crossbeam/bitts';

import { capitalize } from 'lodash';
import { storeToRefs } from 'pinia';
import { computed, onMounted, ref } from 'vue';

import AccountStatusTag from '@/components/billing/AccountStatusTag.vue';
import ChargifyIframes from '@/components/billing/ChargifyIframes.vue';
import SeatBreakdown from '@/components/billing/SeatBreakdown.vue';

import { useChargify } from '@/composables/useChargify';
import { useBillingStore, useFlashesStore } from '@/stores';
import { centsToDollars } from '@/utils';

const { visible = false } = defineProps<{
  visible?: boolean;
}>();

const emit = defineEmits<(e: 'closed') => void>();

const flashesStore = useFlashesStore();
const billingStore = useBillingStore();
const {
  billingPeriod,
  billingPortalURL,
  creditCardLastFourDigits,
  creditCardType,
  grandTotal,
  isSubscriptionCancelled,
  onLegacySubscription,
  lastPaidAmount,
  lastPaidDate,
  subscriptionRenewalDate,
  subscriptionNextBillingDate,
  subscriptionStatus,
} = storeToRefs(billingStore);

const { chargifyClient, chargifyForm, handleGetToken, loading, onLoaded } =
  useChargify();

onMounted(async () => {
  await billingStore.refreshPortalAndPaymentInfo();
  loading.value = false;
  if (!billingStore.creditCardType || !billingStore.creditCardLastFourDigits) {
    flashesStore.addErrorFlash('Could not load credit card information');
    emit('closed');
  }
});

const creditCardText = computed(() => {
  if (creditCardType.value && creditCardLastFourDigits.value) {
    return `${capitalize(creditCardType.value)} (•••• ${creditCardLastFourDigits.value})`;
  }
  return '';
});

function handleViewInvoices() {
  window.open(billingPortalURL.value, '_blank');
}

const cost = computed(() => centsToDollars(grandTotal.value));

const lastPaidAmountReadable = computed(() =>
  centsToDollars(lastPaidAmount.value),
);

const BILLING_SUMMARY = 'billing-summary';
const EDIT_PAYMENT_METHOD = 'edit-payment-method';
const currentTab = ref(BILLING_SUMMARY);
const isOnBillingSummary = computed(() => currentTab.value === BILLING_SUMMARY);
const BILLING_TABS = [
  {
    name: 'Billing Summary',
    value: BILLING_SUMMARY,
  },
  {
    name: 'Edit Payment Method',
    value: EDIT_PAYMENT_METHOD,
  },
];

async function onModalAction() {
  if (isOnBillingSummary.value) {
    emit('closed');
  } else await handleGetToken(() => emit('closed'));
}

function onChangeTab(newTab: string) {
  if (newTab === EDIT_PAYMENT_METHOD) {
    loading.value = true;
  }
  currentTab.value = newTab;
}
</script>

<style lang="pcss" scoped>
@reference "../../css/theme.css";
h1,
h3 {
  @apply font-bold text-neutral-text-strong text-base;
}
h1 {
  @apply text-xl;
}
.o-bitts-modal.bitts-modal--primary:deep() dialog .o-bitts-modal__header {
  @apply p-24 items-start;
}

.view-billing-modal__tab {
  @apply pt-40 pb-16 flex flex-col gap-8;
  .view-billing-modal__summary-section {
    @apply flex items-center justify-between;
  }
}
:deep(iframe) {
  width: 100% !important;
}
</style>
